import type { IngestStatus, LiveStats, MSMERecord, PageSize, Stats } from '../types';

const API_PREFIX = '/api';

class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  if (
    options.body &&
    typeof options.body === 'string' &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(`${API_PREFIX}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!res.ok) {
    let message = `Request failed with status ${res.status}`;
    try {
      const body = (await res.json()) as { message?: string; error?: string };
      message = body.message || body.error || JSON.stringify(body);
    } catch {
      try {
        const text = await res.text();
        if (text) message = text;
      } catch {
        // ignore
      }
    }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

export interface RecordsFilters {
  state?: string;
  nic_code?: string;
  status?: string;
  search?: string;
  limit?: PageSize;
  cursor?: string;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface RecordsResponse {
  records: MSMERecord[];
  nextCursor?: string;
  total?: number;
}

export interface StartIngestResponse {
  jobId: string;
}

export interface PauseIngestResponse {
  success: boolean;
}

export interface User {
  id?: string;
  username?: string;
  role?: string;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export const client = {
  getMe: () => request<User>('/auth/me'),

  getSetupStatus: () => request<{ setupComplete: boolean }>('/auth/setup-status'),

  login: (credentials: LoginCredentials) =>
    request<User>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),

  logout: () =>
    request<{ success: boolean }>('/auth/logout', {
      method: 'POST',
    }),

  setup: (credentials: LoginCredentials & { token: string }) =>
    request<User>('/auth/setup', {
      method: 'POST',
      body: JSON.stringify({ username: credentials.username, password: credentials.password }),
      headers: {
        'X-Setup-Token': credentials.token,
      },
    }),

  getStats: () => request<Stats>('/stats'),

  getLiveStats: () => request<LiveStats>('/stats/live'),

  getRecords: (params: RecordsFilters) => {
    const query = new URLSearchParams();
    if (params.state) query.set('state', params.state);
    if (params.nic_code) query.set('nic_code', params.nic_code);
    if (params.status) query.set('status', params.status);
    if (params.search) query.set('search', params.search);
    if (params.limit) query.set('limit', String(params.limit));
    if (params.cursor) query.set('cursor', params.cursor);
    if (params.sort) query.set('sort', params.sort);
    if (params.order) query.set('order', params.order);

    const qs = query.toString();
    return request<RecordsResponse>(`/records${qs ? `?${qs}` : ''}`);
  },

  getRecord: (id: string) => request<MSMERecord>(`/records/${encodeURIComponent(id)}`),

  startIngest: (payload: {
    resourceId: string;
    totalRecords?: number;
    rateRps?: number;
  }) =>
    request<StartIngestResponse>('/ingest/start', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getIngestStatus: (jobId: string) =>
    request<IngestStatus>(`/ingest/status?id=${encodeURIComponent(jobId)}`),

  pauseIngest: (jobId: string) =>
    request<PauseIngestResponse>(`/ingest/pause?id=${encodeURIComponent(jobId)}`, {
      method: 'POST',
    }),
};
